import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface UnionCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  duplicates: number;
  failedRecords: Array<{ line: number; error: string }>;
}

export function UnionCountyTextParser({ onRecordsSaved }: UnionCountyTextParserProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    
    // M/D/YYYY or MM/DD/YYYY
    const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (match) {
      return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
    }
    return null;
  };

  const extractAmount = (amountStr: string): number | null => {
    if (!amountStr) return null;
    const cleaned = amountStr.replace(/[$,]/g, '');
    const amount = parseFloat(cleaned);
    return isNaN(amount) ? null : amount;
  };

  /**
   * Parse Union County PaxWeb5 search results.
   * The user copies from the left panel which shows results like:
   * 
   * Instrument: 202507160005916    Volume Page: 202507160005916
   * Recorded: 7/16/2025 10:29:35 AM
   * Document Type: LAND CONTRACT    Pages: 6
   * Grantor: BINGMAN, MAX C / BINGMAN, MAX AKA / BINGMAN, JOY L / BINGMAN, JOY AKA
   * Grantee: WHEATLEY, JOHN ROBERT / WORNDLE, STACIA K
   * Legal Description: VMS:3353 Lt/Unit:45 Sub:WOODVIEW PARK SUBDIVISION NUMBER 4 Twnshp:PARIS
   */
  const parseUnionCountyText = (text: string): any[] => {
    const records: any[] = [];
    
    // Split into record blocks - each starts with "Instrument:"
    const blocks = text.split(/(?=Instrument:\s*\d)/i);
    
    for (const block of blocks) {
      if (!block.trim()) continue;
      
      // Extract instrument/document number
      const instrumentMatch = block.match(/Instrument:\s*(\d+)/i);
      if (!instrumentMatch) continue;
      const documentNumber = instrumentMatch[1].trim();
      
      // Extract recording date
      const recordedMatch = block.match(/Recorded:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
      const filingDate = recordedMatch ? parseDate(recordedMatch[1]) : null;
      
      // Extract document type
      const docTypeMatch = block.match(/Document Type:\s*([^\t\n]+?)(?:\s+Pages:|$)/im);
      const documentType = docTypeMatch ? docTypeMatch[1].trim() : '';
      
      // Filter: only land contracts
      const landContractTypes = [
        'LAND CONTRACT', 'CONTRACT FOR DEED', 'INSTALLMENT CONTRACT',
        'LAND INSTALLMENT CONTRACT', 'CONTRACT FOR SALE', 'INSTALLMENT SALE CONTRACT',
        'REAL ESTATE CONTRACT', 'LAND INSTALLMENT'
      ];
      const isLandContract = landContractTypes.some(type =>
        documentType.toUpperCase().includes(type)
      );
      if (!isLandContract) continue;
      
      // Extract pages
      const pagesMatch = block.match(/Pages:\s*(\d+)/i);
      const pages = pagesMatch ? parseInt(pagesMatch[1]) : null;
      
      // Extract grantor (seller)
      const grantorMatch = block.match(/Grantor:\s*(.+?)(?:\n|$)/i);
      const grantorRaw = grantorMatch ? grantorMatch[1].trim() : '';
      
      // Extract grantee (buyer)
      const granteeMatch = block.match(/Grantee:\s*(.+?)(?:\n|$)/i);
      const granteeRaw = granteeMatch ? granteeMatch[1].trim() : '';
      
      // Extract legal description
      const legalMatch = block.match(/Legal Description:\s*(.+?)(?:\n|$)/im);
      let legalDesc = legalMatch ? legalMatch[1].trim() : null;
      // Capture multi-line legal descriptions
      if (legalMatch) {
        const legalIdx = block.indexOf(legalMatch[0]);
        const afterLegal = block.substring(legalIdx + legalMatch[0].length);
        const extraLines = afterLegal.split('\n');
        for (const line of extraLines) {
          const trimmed = line.trim();
          if (!trimmed) break;
          if (/^(Instrument:|Recorded:|Document Type:|Grantor:|Grantee:)/i.test(trimmed)) break;
          legalDesc += ' ' + trimmed;
        }
      }
      
      // Extract consideration/amount if present
      const considerationMatch = block.match(/Consideration:\s*\$?([\d,]+\.?\d*)/i);
      const loanAmount = considerationMatch ? extractAmount(considerationMatch[1]) : null;

      // Clean up names - remove AKA duplicates, normalize
      const cleanName = (raw: string): string => {
        if (!raw) return '';
        // Split by / and deduplicate by last name
        const parts = raw.split('/').map(p => p.trim()).filter(Boolean);
        const seen = new Set<string>();
        const unique: string[] = [];
        for (const part of parts) {
          // Skip AKA entries
          if (/\bAKA\b/i.test(part)) continue;
          const normalized = part.toUpperCase().replace(/\s+/g, ' ');
          if (!seen.has(normalized)) {
            seen.add(normalized);
            unique.push(part);
          }
        }
        return unique.join('; ');
      };
      
      records.push({
        county: 'Union',
        document_number: documentNumber,
        filing_date: filingDate || new Date().toISOString().split('T')[0],
        document_type: documentType,
        grantor_name: cleanName(grantorRaw),
        grantee_name: cleanName(granteeRaw),
        legal_description: legalDesc,
        loan_amount: loanAmount,
        number_of_pages: pages,
        is_potential_owner_finance: true,
        confidence_score: 100,
        detection_reasons: ['Manually imported land contract from Union County'],
        address_needs_review: true,
        status: 'new',
        raw_data: {
          original_grantor: grantorRaw,
          original_grantee: granteeRaw,
        }
      });
    }

    // Sort by filing date descending
    records.sort((a, b) => (b.filing_date || '').localeCompare(a.filing_date || ''));
    
    console.log(`Parsed ${records.length} Union County land contracts`);
    return records;
  };

  const processText = async (text: string) => {
    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const records = parseUnionCountyText(text);

      if (records.length === 0) {
        toast.error("No land contract records found. Make sure you copied the search results from PaxWeb.");
        setIsProcessing(false);
        return;
      }

      const result: ImportResult = { success: 0, failed: 0, duplicates: 0, failedRecords: [] };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          const { data: existing } = await supabase
            .from('county_research')
            .select('id, grantor_address')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            if (existing.grantor_address) {
              result.duplicates++;
              continue;
            }
            // Update existing record without address
            const { error } = await supabase
              .from('county_research')
              .update({
                is_potential_owner_finance: true,
                confidence_score: 100,
                detection_reasons: record.detection_reasons,
                grantor_name: record.grantor_name,
                grantee_name: record.grantee_name,
                legal_description: record.legal_description,
              })
              .eq('id', existing.id);

            if (error) {
              result.failed++;
              result.failedRecords.push({ line: i + 1, error: error.message });
            } else {
              result.success++;
            }
            continue;
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            result.failed++;
            result.failedRecords.push({ line: i + 1, error: error.message });
          } else {
            result.success++;
          }
        } catch (err) {
          result.failed++;
          result.failedRecords.push({ line: i + 1, error: String(err) });
        }
      }

      setImportResult(result);

      if (result.success > 0) {
        toast.success(`Imported ${result.success} Union County records`);
        setPastedText("");
        onRecordsSaved?.();
      }
      if (result.duplicates > 0) {
        toast.info(`${result.duplicates} duplicates skipped (already have addresses)`);
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} records failed`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Import: {importResult.success} succeeded, {importResult.duplicates} duplicates skipped, {importResult.failed} failed
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Union County, OH — Land Contract Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Paste PaxWeb Search Results</label>
            <Textarea
              placeholder={`Paste Union County search results here...\n\nExample:\nInstrument: 202507160005916    Volume Page: 202507160005916\nRecorded: 7/16/2025 10:29:35 AM\nDocument Type: LAND CONTRACT    Pages: 6\nGrantor: BINGMAN, MAX C\nGrantee: WHEATLEY, JOHN ROBERT\nLegal Description: VMS:3353 Lt/Unit:45...`}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              disabled={isProcessing}
              rows={12}
              className="font-mono text-xs"
            />
            <Button
              onClick={() => processText(pastedText)}
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Land Contracts
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">Processing... {progress}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>How to use:</strong> Go to Union County PaxWeb5 recorder site, search for Land Contracts,
          then select all results text and paste here. The parser extracts instrument numbers, dates,
          grantor/grantee names, and legal descriptions.
          <br /><br />
          <strong>Screenshot Bot:</strong> After importing, use the Screenshot Bot tab to paste full screenshots
          of each record (both panels) to extract seller addresses, purchase prices, and financial terms.
        </AlertDescription>
      </Alert>
    </div>
  );
}
